import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Menu, X, LogOut, User, Shield, Inbox, Users, Edit3, KeyRound, CalendarDays, MessageSquare } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Header() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMsgUnread();
      const interval = setInterval(fetchMsgUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchUnreadCount = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/inbox/unread-count`);
      setUnreadCount(data.count);
    } catch {}
  };

  const fetchMsgUnread = async () => {
    try {
      const { data } = await axios.get(`${API}/messages/unread-count`);
      setMsgUnread(data.count);
    } catch {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/rules', label: 'Rules' },
    { href: '/teams', label: 'Teams' },
    { href: '/clans', label: 'Clans' },
    { href: '/players', label: 'Players' },
    { href: '/bracket', label: 'Bracket' },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header border-b border-white/10" data-testid="header">
      <div className="w-full px-6 py-4">
        <div className="flex items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="logo-link">
            <span className="font-heading text-xl font-bold text-cyan-400 tracking-tight">
              A.C.C.T
            </span>
          </Link>

          {/* Desktop Nav - centered */}
          <nav className="hidden md:flex items-center gap-8 flex-1 justify-center" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className="text-sm font-medium text-zinc-400 hover:text-cyan-400 transition-colors uppercase tracking-wide"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons - far right */}
          <div className="hidden md:flex items-center gap-4 flex-shrink-0" data-testid="auth-buttons">
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="border-zinc-800 bg-transparent text-white hover:bg-zinc-900 hover:text-cyan-400 rounded-none relative"
                    data-testid="user-menu-trigger"
                  >
                    {isAdmin && <Shield className="w-4 h-4 mr-2 text-yellow-500" />}
                    <User className="w-4 h-4 mr-2" />
                    {user?.name || 'User'}
                    {isAdmin && unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {unreadCount + msgUnread}
                      </span>
                    )}
                    {!isAdmin && msgUnread > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                        {msgUnread}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#121212] border-zinc-800 rounded-none w-48">
                  {isAdmin && (
                    <>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/inbox')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                        data-testid="admin-inbox-link"
                      >
                        <Inbox className="w-4 h-4 mr-2" />
                        Inbox
                        {unreadCount > 0 && (
                          <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {unreadCount}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/users')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                        data-testid="admin-users-link"
                      >
                        <Users className="w-4 h-4 mr-2" />
                        User Accounts
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/edit')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                        data-testid="admin-edit-link"
                      >
                        <Edit3 className="w-4 h-4 mr-2" />
                        Edit Site
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin/events')}
                        className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                        data-testid="admin-events-link"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        Events
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                    </>
                  )}
                  <DropdownMenuItem 
                    onClick={() => navigate('/messages')}
                    className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                    data-testid="player-messages-link"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Messages
                    {msgUnread > 0 && (
                      <span className="ml-auto bg-cyan-500 text-black text-xs px-2 py-0.5 rounded-full">
                        {msgUnread}
                      </span>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => navigate('/change-password')}
                    className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                    data-testid="change-password-link"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={handleLogout}
                    className="text-zinc-300 hover:text-white hover:bg-zinc-900 cursor-pointer rounded-none"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button 
                    variant="ghost" 
                    className="text-zinc-400 hover:text-white hover:bg-zinc-900 rounded-none"
                    data-testid="login-nav-button"
                  >
                    Login
                  </Button>
                </Link>
                <Link to="/register">
                  <Button 
                    className="bg-cyan-400 text-black font-bold uppercase tracking-wide hover:bg-cyan-300 rounded-none px-6"
                    data-testid="join-tournament-button"
                  >
                    Join Tournament
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-zinc-800 pt-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm font-medium text-zinc-400 hover:text-cyan-400 transition-colors uppercase tracking-wide"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <>
                      <Link to="/admin/inbox" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                        Inbox {unreadCount > 0 && `(${unreadCount})`}
                      </Link>
                      <Link to="/admin/users" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                        User Accounts
                      </Link>
                      <Link to="/admin/edit" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                        Edit Site
                      </Link>
                      <Link to="/admin/events" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                        Events
                      </Link>
                    </>
                  )}
                  <Link to="/messages" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                    Messages {msgUnread > 0 && `(${msgUnread})`}
                  </Link>
                  <Link to="/change-password" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                    Change Password
                  </Link>
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="text-sm font-medium text-zinc-400 hover:text-red-400 text-left">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-zinc-400 hover:text-cyan-400" onClick={() => setMobileMenuOpen(false)}>
                    Login
                  </Link>
                  <Link to="/register" className="text-sm font-medium text-cyan-400 hover:text-cyan-300" onClick={() => setMobileMenuOpen(false)}>
                    Join Tournament
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
